/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from 'antd';
import { setTypeMail } from '../actions';
import inboxImage from 'assets/bw_image/icon/i__inbox.svg';
import emailImage from 'assets/bw_image/icon/i__email.svg';
import emailStarImage from 'assets/bw_image/icon/i__email_star.svg';
import trashImage from 'assets/bw_image/icon/i__trash.svg';
import { getMailGroups, getOptionUser } from '../helpers/call-api';
import MailGroupModal from '../components/MailGroupModal';
import { formatUserLabel } from '../utils/userLabel';
import { showToast } from 'utils/helpers';
import useVerifyAccess from 'hooks/useVerifyAccess';
import '../style.scss';

const MAIL_MENU_MIN_WIDTH = 220;
const MAIL_MENU_MAX_WIDTH = 520;
const MAIL_MENU_DEFAULT_WIDTH = 300;
const MAIL_MENU_WIDTH_STORAGE_KEY = 'mailMenuWidth';

const getStoredMenuWidth = () => {
  const storedValue = localStorage.getItem(MAIL_MENU_WIDTH_STORAGE_KEY);
  if (!storedValue) return MAIL_MENU_DEFAULT_WIDTH;
  const storedWidth = Number(storedValue);
  if (!Number.isFinite(storedWidth)) return MAIL_MENU_DEFAULT_WIDTH;
  return Math.min(MAIL_MENU_MAX_WIDTH, Math.max(MAIL_MENU_MIN_WIDTH, storedWidth));
};

const MailMenu = ({ typeMail: propTypeMail }) => {
  const dispatch = useDispatch();
  const menuRef = useRef(null);
  const menuWidthRef = useRef(MAIL_MENU_DEFAULT_WIDTH);

  const countNotRead = useSelector((state) => state.mailbox.countNotRead);
  const typeMail = useSelector((state) => state.mailbox.typeMail);

  const history = useHistory();
  const { verifyPermission } = useVerifyAccess();
  const canCreateMail = verifyPermission('SYS_MAIL_CREATE');
  const [menuWidth, setMenuWidth] = useState(getStoredMenuWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [type, setType] = useState(null);
  const [isGroupOpen, setIsGroupOpen] = useState(true);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [mailGroups, setMailGroups] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  const handleCreateCompose = () => {
    history.push(`/mail/compose`);
  };

  const getGroupData = useCallback(async () => {
    try {
      const groupRes = await getMailGroups();
      setMailGroups(groupRes || []);
    } catch (error) {
      showToast.error(error?.message || 'Không tải được danh sách nhóm người nhận.');
    }
  }, []);

  const getUserData = useCallback(async () => {
    try {
      const userRes = await getOptionUser();
      setUserOptions(
        (userRes || []).map(({ id, name }) => ({
          value: `USER-${id}`,
          label: formatUserLabel(id, name),
        })),
      );
    } catch (error) {
      showToast.error(error?.message || 'Không tải được danh sách người dùng.');
    }
  }, []);

  const handleChangeRouter = ({ typeMail }) => {
    localStorage.setItem('typeMail', typeMail);
    dispatch(setTypeMail(typeMail));
    if (typeMail === 'inbox') {
      history.push(`/mail`);
    } else {
      history.push(`/mail/${typeMail}`);
    }
  };

  const handleComposeGroup = (groupId) => {
    history.push(`/mail/compose?recipient=${encodeURIComponent(`GROUP-${groupId}`)}`);
  };

  const handleOpenGroupModal = () => {
    setIsGroupModalOpen(true);
  };

  const updateMenuWidth = useCallback((nextWidth) => {
    const layoutWidth = menuRef.current?.parentElement?.getBoundingClientRect().width || window.innerWidth;
    const availableMaxWidth = Math.max(MAIL_MENU_MIN_WIDTH, layoutWidth - 480);
    const clampedWidth = Math.min(MAIL_MENU_MAX_WIDTH, availableMaxWidth, Math.max(MAIL_MENU_MIN_WIDTH, nextWidth));
    menuWidthRef.current = clampedWidth;
    setMenuWidth(clampedWidth);
    return clampedWidth;
  }, []);

  const handleResizeKeyDown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    let nextWidth = menuWidthRef.current;
    if (event.key === 'ArrowLeft') nextWidth -= 16;
    if (event.key === 'ArrowRight') nextWidth += 16;
    if (event.key === 'Home') nextWidth = MAIL_MENU_MIN_WIDTH;
    if (event.key === 'End') nextWidth = MAIL_MENU_MAX_WIDTH;

    localStorage.setItem(MAIL_MENU_WIDTH_STORAGE_KEY, `${updateMenuWidth(nextWidth)}`);
  };

  useEffect(() => {
    menuWidthRef.current = menuWidth;
  }, [menuWidth]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const handlePointerMove = (event) => {
      const layoutLeft = menuRef.current?.parentElement?.getBoundingClientRect().left || 0;
      updateMenuWidth(event.clientX - layoutLeft);
    };

    const handlePointerUp = () => {
      localStorage.setItem(MAIL_MENU_WIDTH_STORAGE_KEY, `${menuWidthRef.current}`);
      setIsResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp, { once: true });

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isResizing, updateMenuWidth]);

  useEffect(() => {
    if (propTypeMail) {
      setType(propTypeMail);
      return;
    }
    const storedTypeMail = localStorage.getItem('typeMail');
    if (storedTypeMail && typeMail) {
      if (storedTypeMail === typeMail) {
        setType(typeMail);
      } else {
        setType(storedTypeMail);
      }
    }
  }, [propTypeMail, typeMail]);

  useEffect(() => {
    getGroupData();
  }, [getGroupData]);

  useEffect(() => {
    if (isGroupModalOpen && !userOptions.length) {
      getUserData();
    }
  }, [getUserData, isGroupModalOpen, userOptions.length]);
  // localStorage.getItem('type')

  return (
    <div
      ref={menuRef}
      className={`bw_col_3 bw_left_mail ${isResizing ? 'bw_mail_menu_resizing' : ''}`}
      style={{ '--mail-menu-width': `${menuWidth}px` }}>
      {canCreateMail ? (
        <span className='bw_btn bw_btn_success bw_send_mail' onClick={() => handleCreateCompose()}>
          <span className='fi fi-rr-envelope-plus'></span>
          <p className='bw_btn_text_mail'>Soạn thư</p>
        </span>
      ) : null}
      <ul className='bw_control_form bw_action_email'>
        <li onClick={() => handleChangeRouter({ typeMail: 'inbox' })}>
          <a className={type === 'inbox' ? 'bw_active' : ''}>
            <img src={inboxImage} /> Hộp thư đến
            {countNotRead > 0 && <span>{countNotRead}</span>}
          </a>
        </li>
        <li onClick={() => handleChangeRouter({ typeMail: 'send' })}>
          <a className={type === 'send' ? 'bw_active' : ''}>
            <img src={emailImage} /> Hộp thư đi
          </a>
        </li>
        <li onClick={() => handleChangeRouter({ typeMail: 'flagged' })}>
          <a className={type === 'flagged' ? 'bw_active' : ''}>
            <img src={emailStarImage} /> Thư đánh dấu
          </a>
        </li>
        <li onClick={() => handleChangeRouter({ typeMail: 'draft' })}>
          <a className={type === 'draft' ? 'bw_active' : ''}>
            <img src={emailImage} /> Thư nháp
          </a>
        </li>
        <li onClick={() => handleChangeRouter({ typeMail: 'trash' })}>
          <a className={type === 'trash' ? 'bw_active' : ''}>
            <img src={trashImage} /> Thùng rác
          </a>
        </li>
        <li onClick={() => handleChangeRouter({ typeMail: 'address-book' })}>
          <a className={type === 'address-book' ? 'bw_active' : ''}>
            <i className='fi fi-rr-address-book bw_mail_menu_icon' /> Danh bạ & nhóm
          </a>
        </li>
        <li className='bw_mail_group_menu'>
          <div className='bw_mail_group_header'>
            <button
              type='button'
              className='bw_mail_group_toggle'
              onClick={() => setIsGroupOpen((prev) => !prev)}>
              <i className={`fi ${isGroupOpen ? 'fi-rr-angle-small-down' : 'fi-rr-angle-small-right'}`} />
              Nhóm người nhận
            </button>
            {canCreateMail ? (
              <Tooltip title='Thêm mới nhóm'>
                <button type='button' className='bw_mail_group_add' onClick={handleOpenGroupModal}>
                  <i className='fi fi-rr-plus-small' />
                </button>
              </Tooltip>
            ) : null}
          </div>
          {isGroupOpen && (
            <div className='bw_mail_group_list'>
              {canCreateMail ? (
                <button type='button' className='bw_mail_group_empty' onClick={handleOpenGroupModal}>
                  <i className='fi fi-rr-plus-small' />
                  Thêm mới nhóm
                </button>
              ) : null}
              {mailGroups.length ? (
                mailGroups.map((group) => (
                  <button
                    type='button'
                    key={group.mail_group_id}
                    className='bw_mail_group_item'
                    disabled={!canCreateMail}
                    onClick={() => handleComposeGroup(group.mail_group_id)}>
                    <i className='fi fi-rr-envelope' />
                    <span>{group.group_name}</span>
                    <small>{group.total_user || 0}</small>
                  </button>
                ))
              ) : (
                <span className='bw_mail_group_no_data'>Chưa có nhóm</span>
              )}
            </div>
          )}
        </li>
      </ul>
      {canCreateMail ? (
        <MailGroupModal
          open={isGroupModalOpen}
          onClose={() => setIsGroupModalOpen(false)}
          userOptions={userOptions}
          groups={mailGroups}
          onSaved={getGroupData}
        />
      ) : null}
      <button
        type='button'
        className='bw_mail_menu_resizer'
        aria-label='Thay đổi độ rộng menu hộp thư'
        title='Kéo để thay đổi độ rộng menu'
        onPointerDown={(event) => {
          event.preventDefault();
          setIsResizing(true);
        }}
        onKeyDown={handleResizeKeyDown}
      />
    </div>
  );
};

export default MailMenu;
